import { useUser } from "@clerk/nextjs";
import { useState, useCallback, useRef, useEffect } from "react";
import { AUTH_ERRORS, VERIFICATION_CODE_LENGTH } from "../utils/authConstants";
import { formatVerificationCode, getErrorMessage } from "../utils/authHelpers";
import { logAuthError } from "../utils/authErrorLogging";

const HOOK_NAME = "useAddEmailVerification";

enum ADD_EMAIL_ERROR_TYPES {
  CREATE_EMAIL = "create_email",
  SEND_CODE = "send_code",
  VERIFY_CODE = "verify_code",
  RESEND_CODE = "resend_code",
}

export enum AddEmailVerificationStep {
  EMAIL_INPUT = "email_input",
  CODE_VERIFICATION = "code_verification",
  VERIFIED = "verified",
}

interface UseAddEmailVerificationReturn {
  step: AddEmailVerificationStep;
  verificationCode: string;
  loading: boolean;
  error: string | null;
  setVerificationCode: (code: string) => void;
  sendVerificationCode: () => Promise<void>;
  verifyCode: () => Promise<void>;
  resendCode: () => Promise<void>;
  reset: () => void;
}

/**
 * Hook to manage adding and verifying a new email address to an existing user account.
 * @param email - The email address to add and verify
 */
export function useAddEmailVerification(
  email: string
): UseAddEmailVerificationReturn {
  const { user, isLoaded } = useUser();

  const [step, setStep] = useState<AddEmailVerificationStep>(
    AddEmailVerificationStep.EMAIL_INPUT
  );
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingEmailAddress, setPendingEmailAddress] = useState<any>(null);
  const verificationSentForEmail = useRef<string | null>(null);
  const isSending = useRef(false);

  const sendVerificationCode = useCallback(async () => {
    if (!isLoaded || !user || !email) return;

    // Skip if we've already sent verification for this email or if we're currently sending
    if (verificationSentForEmail.current === email || isSending.current) {
      return;
    }

    // Mark that we're sending to prevent concurrent calls
    isSending.current = true;
    verificationSentForEmail.current = email;

    // Check if email already exists on the account
    const existingEmail = user.emailAddresses.find(
      (emailAddr) =>
        emailAddr.emailAddress.toLowerCase() === email.toLowerCase()
    );

    if (existingEmail) {
      if (existingEmail.verification.status === "verified") {
        // Email already verified, skip to verified state
        setStep(AddEmailVerificationStep.VERIFIED);
        return;
      } else {
        // Email exists but not verified, use existing email object
        setPendingEmailAddress(existingEmail);
      }
    }

    setLoading(true);
    setError(null);

    try {
      let emailAddress = existingEmail;

      // Create new email address if it doesn't exist
      if (!emailAddress) {
        emailAddress = await user.createEmailAddress({
          email,
        });
        setPendingEmailAddress(emailAddress);
      }

      // Send verification code
      await emailAddress.prepareVerification({
        strategy: "email_code",
      });

      setStep(AddEmailVerificationStep.CODE_VERIFICATION);
    } catch (err) {
      // Reset flags on error so user can retry
      isSending.current = false;
      verificationSentForEmail.current = null;
      console.error("Failed to send verification code:", err);
      const errorMsg = getErrorMessage(err, "Failed to send verification code");
      setError(errorMsg);
      logAuthError({
        error: err,
        errorType: ADD_EMAIL_ERROR_TYPES.SEND_CODE,
        hookName: HOOK_NAME,
        context: { email },
      });
    } finally {
      setLoading(false);
      // Reset sending flag after completion
      isSending.current = false;
    }
  }, [email, user, isLoaded]);

  const verifyCodeInternal = useCallback(
    async (code: string) => {
      if (!pendingEmailAddress) return;

      setLoading(true);
      setError(null);

      try {
        const result = await pendingEmailAddress.attemptVerification({
          code,
        });

        if (result.verification.status === "verified") {
          setStep(AddEmailVerificationStep.VERIFIED);
          // Reload user to get updated email addresses
          await user?.reload();
        } else {
          const errorMsg = "Verification failed. Please try again.";
          setError(errorMsg);
          logAuthError({
            error: `Verification status: ${result.verification.status}`,
            errorType: ADD_EMAIL_ERROR_TYPES.VERIFY_CODE,
            hookName: HOOK_NAME,
            context: {
              email,
              status: result.verification.status,
            },
          });
          // Clear the code so user can try again
          setVerificationCode("");
        }
      } catch (err) {
        console.error("Failed to verify code:", err);
        const errorMsg = getErrorMessage(err, AUTH_ERRORS.VERIFICATION_ERROR);
        setError(errorMsg);
        logAuthError({
          error: err,
          errorType: ADD_EMAIL_ERROR_TYPES.VERIFY_CODE,
          hookName: HOOK_NAME,
          context: {
            email,
            codeLength: code.length,
          },
        });
        // Clear the code so user can try again
        setVerificationCode("");
      } finally {
        setLoading(false);
      }
    },
    [pendingEmailAddress, email, user]
  );

  const verifyCode = useCallback(async () => {
    await verifyCodeInternal(verificationCode);
  }, [verificationCode, verifyCodeInternal]);

  const handleSetVerificationCode = useCallback(
    (code: string) => {
      const formattedCode = formatVerificationCode(code);
      setVerificationCode(formattedCode);
      // Clear error when user starts typing
      if (error && code.length > 0) {
        setError(null);
      }
      // Auto-submit when code is complete
      if (formattedCode.length === VERIFICATION_CODE_LENGTH && !loading) {
        verifyCodeInternal(formattedCode);
      }
    },
    [error, loading, verifyCodeInternal]
  );

  const resendCode = useCallback(async () => {
    if (!pendingEmailAddress) return;

    setLoading(true);
    setError(null);

    try {
      await pendingEmailAddress.prepareVerification({
        strategy: "email_code",
      });
      // Clear the code input
      setVerificationCode("");
    } catch (err) {
      const errorMsg = getErrorMessage(
        err,
        "Failed to resend verification code"
      );
      setError(errorMsg);
      logAuthError({
        error: err,
        errorType: ADD_EMAIL_ERROR_TYPES.RESEND_CODE,
        hookName: HOOK_NAME,
        context: { email },
      });
    } finally {
      setLoading(false);
    }
  }, [pendingEmailAddress, email]);

  const reset = useCallback(() => {
    setStep(AddEmailVerificationStep.EMAIL_INPUT);
    setVerificationCode("");
    setError(null);
    setPendingEmailAddress(null);
    verificationSentForEmail.current = null;
    isSending.current = false;
  }, []);

  // Initialize verification send on first render with valid email
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (email && isLoaded && user && !hasInitialized.current) {
      hasInitialized.current = true;
      sendVerificationCode();
    }
  }, [email, isLoaded, user, sendVerificationCode]);

  return {
    step,
    verificationCode,
    loading,
    error,
    setVerificationCode: handleSetVerificationCode,
    sendVerificationCode,
    verifyCode,
    resendCode,
    reset,
  };
}
